import React, { useState, useEffect, useMemo } from 'react';
import { Gem, User, Search, FileText } from 'lucide-react';
import PdfService from '../../services/PdfService';
import LoadEstimateModal from '../shared/LoadEstimateModal';
import PaymentConfirmModal from './PaymentConfirmModal';
import PageIdentifier from '../shared/PageIdentifier';
import CostPanel, { RatePill } from '../shared/CostPanel';
import SCREEN_IDS from '../../utils/screenIds';
import CustomerLookup from '../shared/CustomerLookup';
import WhatsAppLinkService from '../../services/WhatsAppLinkService';
import EmailSendService from '../../services/EmailSendService';
import HistoricalRateService from '../../services/HistoricalRateService';
import EstimateDetailModal from '../estimates/EstimateDetailModal';
import { applyExchangeRate } from '../../utils/calculations';
import {
  calculatePurityGoldRate,
  calculateSaleNetWeight,
  isKundanItemCode,
  isKundanCategory,
  calculateFineWeight,
  calculateGoldValue,
  calculateWastageAmount,
  calculateMakingAmount,
  calculateTotalGoldAmount,
  calculateCertificationCharges,
  calculateJewelValue,
  calculateINRGrandTotal,
  stoneWeightToGrams,
  buildStoneCategoryTotals,
  calculateAcquisitionCostWithTax,
  calculateKundanFineWeight,
  calculateCostPanelResult,
  isSmallItemCode,
  calculateSmallItemPrice,
} from '../../shared/utils/jewelryCalculations';
import usePermissions from '../../hooks/usePermissions';
import { todayCentral } from '../../shared/utils/dateUtils';

// Blur (remove focus) from any input when Enter is pressed
const blurOnEnter = (e) => { if (e.key === 'Enter') e.target.blur(); };

const IndianBilling = () => {
  // Ref for Item Code input
  const itemCodeRef = React.useRef(null);

  // Auto-focus item code field on page load
  useEffect(() => { itemCodeRef.current?.focus(); }, []);

  // Session-based authentication
  const { hasPermission } = usePermissions();
  const canEditRate = hasPermission('indian-billing', 'edit');

  // Categories for wastage/making lookup
  const [categories, setCategories] = useState([]);

  // Section 1: Header & Rates
  const [date, setDate] = useState(() => {
    const today = new Date();
    // Get local date in YYYY-MM-DD format
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [goldRate, setGoldRate] = useState('');
  const [goldRateEditable, setGoldRateEditable] = useState(false);
  // Today's live gold rate, captured once at mount — used to detect when goldRate above
  // has been overridden by a backdated Bill Date (historical rate lookup).
  const [todaysGoldRate, setTodaysGoldRate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dollarRate, setDollarRate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Section 2: Jewelry & Gold Calculation
  const [itemCode, setItemCode] = useState('');
  const [jewelryName, setJewelryName] = useState('');
  const [purity, setPurity] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [fineWeight, setFineWeight] = useState('');
  const [finePercent, setFinePercent] = useState(null); // Fine % from jewelry_pieces (Kundan only)
  const [kundanWeight, setKundanWeight] = useState('');
  const [kundanPercent, setKundanPercent] = useState('');
  const [goldPrice, setGoldPrice] = useState('');
  const [goldValue, setGoldValue] = useState('');
  const [wastagePercent, setWastagePercent] = useState('');
  const [wastageAmount, setWastageAmount] = useState('');
  const [makingCharge, setMakingCharge] = useState('');
  const [makingAmount, setMakingAmount] = useState('');
  const [totalGoldAmount, setTotalGoldAmount] = useState('');

  // Jewelry image (for PDF watermark)
  const [jewelryImage, setJewelryImage] = useState(null);

  // Item code lookup status: null | 'found' | 'sold' | 'not_found'
  const [itemCodeStatus, setItemCodeStatus] = useState(null);
  const [itemCodeStatusMsg, setItemCodeStatusMsg] = useState('');

  // Stone calculations
  const [stoneTotal, setStoneTotal] = useState(0);
  const [stoneWeightTotal, setStoneWeightTotal] = useState(0);
  const [stonePurchaseCost, setStonePurchaseCost] = useState(0);
  const [fetchedStones, setFetchedStones] = useState([]);
  const [certificateDocuments, setCertificateDocuments] = useState([]);

  // Grand Total (lifted from TotalSection for WhatsApp sharing)
  const [grandTotal, setGrandTotal] = useState('0.00');

  // Linked customer id (set when a customer is selected from lookup)
  const [customerId, setCustomerId] = useState(null);

  // Payment rows (collected from PaymentSection, posted after bill is saved)
  const [payments, setPayments] = useState([]);

  // Payment confirm modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Totals lifted for PDF generation
  const [pdfTotals, setPdfTotals] = useState({ gst: 3, taxTotal: '0.00', certCharges: 0 });

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Estimate pre-fill — set when converting from ViewEstimates
  const [estimateNumber, setEstimateNumber] = useState('');
  const [estimateId, setEstimateId] = useState(null);
  const [estimateDiscount, setEstimateDiscount] = useState(0);
  const [estimateGstPct, setEstimateGstPct] = useState(null);
  const [estimateBannerVisible, setEstimateBannerVisible] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  // Bumped on Clear to force TotalSection to remount, resetting its local
  // state (discount, GST%, etc.) that has no other reset path.
  const [formResetKey, setFormResetKey] = useState(0);

  // Certification charges - similar to USBilling
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [diamondCarats, setDiamondCarats] = useState(0);
  const [certificationRequired, setCertificationRequired] = useState(false);
  const [laborCharges, setLaborCharges] = useState(0);
  const [otherCosts, setOtherCosts] = useState(0); // jewelry_pieces.other_costs (e.g. hallmarking) — Cost Panel only
  const [inventoryNetWeight, setInventoryNetWeight] = useState(''); // physical jewelry_pieces.net_weight — Cost Panel only

  // Fetch rates and categories on mount
  const [goldRateError, setGoldRateError] = useState('');
  const [goldRateWarn, setGoldRateWarn] = useState('');

  useEffect(() => {
    const STALE_DAYS = 2;
    const daysSince = (dateStr) => dateStr
      ? Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
      : null;

    // Fetch gold rate
    fetch('/api/rates/gold/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate && data.rate.gold_24k != null && data.rate.gold_24k !== '') {
          setGoldRate(parseFloat(data.rate.gold_24k));
          setTodaysGoldRate(parseFloat(data.rate.gold_24k));
          setGoldRateEditable(false);
          const age = daysSince(data.rate.rate_date);
          if (!data.is_today && age !== null && age > STALE_DAYS) {
            setGoldRateError('');
            setGoldRateWarn('');
            setGoldRateEditable(true);
          } else if (!data.is_today) {
            setGoldRateWarn(`ℹ️ ${data.message}. Rate is auto-filled — you can edit if needed.`);
            setGoldRateError('');
            setGoldRateEditable(true);
          } else {
            setGoldRateError('');
            setGoldRateWarn('');
          }
        } else {
          setGoldRate('');
          setGoldRateError('Gold rate not found. Please enter today\'s rate in Manual Rate Entry (UTIL-003) or enter manually below.');
          setGoldRateWarn('');
          setGoldRateEditable(true);
        }
      })
      .catch(() => {
        // Network/server error reaching our own API — keep showing whatever rate was
        // already loaded (last known good rate) instead of blanking the field.
        setGoldRateError('Failed to fetch latest gold rate. Showing last known rate — please verify or enter manually.');
        setGoldRateWarn('');
        setGoldRateEditable(true);
      });

    // Fetch dollar rate
    fetch('/api/rates/dollar/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate) {
          const rawRate = parseFloat(data.rate.usd_to_inr);
          setDollarRate(applyExchangeRate(rawRate));
        }
      })
      .catch(() => {
        // Keep default value on error
      });

    // Fetch categories
    fetch('/api/categories', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          console.log('Categories loaded:', data); // Debug log
          setCategories(data);
        } else {
          setCategories([
            { id: 1, name: 'Rings', code: 'RNG', wastage_charges: 8, making_charges: 500 },
            { id: 2, name: 'Necklaces', code: 'DNS', wastage_charges: 10, making_charges: 800 },
            { id: 3, name: 'Earrings', code: 'EAR', wastage_charges: 6, making_charges: 400 }
          ]);
        }
      })
      .catch(() => {
        setCategories([
          { id: 1, name: 'Rings', code: 'RNG', wastage_charges: 8, making_charges: 500 },
          { id: 2, name: 'Necklaces', code: 'DNS', wastage_charges: 10, making_charges: 800 },
          { id: 3, name: 'Earrings', code: 'EAR', wastage_charges: 6, making_charges: 400 }
        ]);
      });
  }, []);

  // ── Estimate pre-fill on mount ────────────────────────────
  useEffect(() => {
    const prefill = window.__estimatePreFill;
    if (!prefill || prefill.currency !== 'INR') return;

    // Consume the payload
    window.__estimatePreFill = null;

    // Customer
    setName(prefill.customerName || '');
    setPhone(prefill.customerPhone || '');
    setEmail(prefill.customerEmail || '');

    // Rates (may be overridden)
    if (prefill.goldRate) {
      setGoldRate(parseFloat(prefill.goldRate));
      setGoldRateEditable(true);
    }

    // Item code — auto-fetch after categories load. Stone rates/wastage/making
    // are passed as overrides applied AFTER the fetch completes (not set
    // separately beforehand) — fetchJewelryDetails's own stone-fetch would
    // otherwise silently overwrite them a moment later.
    if (prefill.itemCode) {
      setItemCode(prefill.itemCode);
      const stoneOverrides = Array.isArray(prefill.stoneDetails) && prefill.stoneDetails.length > 0
        ? prefill.stoneDetails
        : null;
      setTimeout(() => fetchJewelryDetails(prefill.itemCode, {
        stones: stoneOverrides,
        wastage: prefill.wastage,
        making: prefill.making,
      }), 300);
    } else if (Array.isArray(prefill.stoneDetails) && prefill.stoneDetails.length > 0) {
      setFetchedStones(prefill.stoneDetails);
      if (prefill.making) setMakingCharge(String(prefill.making));
    }

    // GST % and discount — exactly what was finalized with the customer on
    // the estimate, not this screen's own default.
    if (prefill.customsPct) {
      setEstimateGstPct(parseFloat(prefill.customsPct));
    }
    setEstimateDiscount(parseFloat(prefill.discount) || 0);

    setEstimateNumber(prefill.estimateNumber || '');
    setEstimateId(prefill.estimateId || null);
    setEstimateBannerVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-calculate net weight — includes the same purity-converted Kundan
  // weight adjustment as USDEstimate.js for Kundan (K-prefixed) items.
  useEffect(() => {
    const nw = calculateSaleNetWeight({ grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity });
    setNetWeight(parseFloat(grossWeight) > 0 ? nw.toFixed(3) : '');
  }, [grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity]);

  // Fine weight
  useEffect(() => {
    if (netWeight && purity) {
      setFineWeight(calculateFineWeight(netWeight, purity).toFixed(3));
    } else {
      setFineWeight('');
    }
  }, [netWeight, purity]);

  // Purity-adjusted gold price
  useEffect(() => {
    if (goldRate && purity) {
      setGoldPrice(calculatePurityGoldRate(goldRate, purity).toFixed(2));
    } else {
      setGoldPrice('');
    }
  }, [goldRate, purity]);

  // Gold value
  useEffect(() => {
    if (goldPrice && netWeight) {
      setGoldValue(calculateGoldValue(goldPrice, netWeight).toFixed(2));
    } else {
      setGoldValue('');
    }
  }, [goldPrice, netWeight]);

  // Wastage amount (always rounds up)
  useEffect(() => {
    if (netWeight && wastagePercent && goldPrice) {
      setWastageAmount(calculateWastageAmount(netWeight, wastagePercent, goldPrice).toFixed(3));
    } else {
      setWastageAmount('');
    }
  }, [netWeight, wastagePercent, goldPrice]);

  // Making amount
  useEffect(() => {
    if (netWeight && makingCharge) {
      setMakingAmount(calculateMakingAmount(netWeight, makingCharge).toFixed(2));
    } else {
      setMakingAmount('');
    }
  }, [netWeight, makingCharge]);

  // Total gold amount
  useEffect(() => {
    setTotalGoldAmount(calculateTotalGoldAmount(goldValue, wastageAmount, makingAmount).toFixed(2));
  }, [goldValue, wastageAmount, makingAmount]);

  // Date validation
  const handleDateChange = async (e) => {
    const val = e.target.value;
    const today = todayCentral();
    if (val > today) return; // Block future dates
    setDate(val);
    if (!val) return;

    try {
      const { gold, dollar } = await HistoricalRateService.getRates(val);
      const missing = [];
      if (gold?.gold_24k != null && gold.gold_24k !== '') {
        setGoldRate(parseFloat(gold.gold_24k));
        setGoldRateError('');
      } else {
        missing.push('gold');
      }
      if (dollar?.usd_to_inr != null && dollar.usd_to_inr !== '') {
        setDollarRate(applyExchangeRate(parseFloat(dollar.usd_to_inr)));
      } else {
        missing.push('dollar');
      }
      setGoldRateWarn(missing.length
        ? `No ${missing.join(' or ')} rate saved for ${val}. Existing values were kept.`
        : `Gold and dollar rates updated for ${val}.`);
      setGoldRateEditable(true);
    } catch (error) {
      setGoldRateError(error.message || 'Failed to fetch rates for selected date.');
      setGoldRateWarn('');
      setGoldRateEditable(true);
    }
  };

  // Fetch jewelry details by Item Code
  // estimateOverrides — if provided, applied AFTER all system defaults so they always win
  const fetchJewelryDetails = async (code, estimateOverrides = null) => {
    if (!code) return;
    let itemFound = false;
    try {
      const normalizedCode = code.trim().toUpperCase();

      // Extract category code from item code (e.g., DNS-1 -> DNS)
      const categoryCode = normalizedCode.split('-')[0];

      const res = await fetch(`/api/jewelry/details/${normalizedCode}`, { credentials: 'include' });
      const data = await res.json();
      if (data && data.code === normalizedCode) {
        // Block billing for any item that isn't In Stock — Sold, Reserved
        // (mid-checkout on another bill), Under Repair, etc. The 409 payload
        // only carries {error, status, code, message}, not the full item, so
        // this must be checked before assuming itemFound and reading fields
        // (gross_weight, stones, ...) that don't exist on this response.
        if (res.status === 409 || data.error === 'item_status') {
          setItemCodeStatus('sold');
          setItemCodeStatusMsg(data.message || `Item ${data.status || 'unavailable'}`);
          // Clear any auto-populated fields
          setJewelryName('');
          setPurity('');
          setGrossWeight('');
          setNetWeight('');
          setFinePercent(null);
          setFetchedStones([]);
          setCertificateDocuments([]);
          setDiamondCarats(0);
          setCertificationRequired(false);
          setLaborCharges(0); setOtherCosts(0); setInventoryNetWeight('');
          setCategoryInfo(null);
          setWastagePercent('');
          setMakingCharge('');
          itemCodeRef.current?.focus();
          return;
        }

        itemFound = true;
        setItemCodeStatus(null);
        setItemCodeStatusMsg('');
        setJewelryName(data.description?.trim() || data.code || ''); // description, else item code
        setPurity(data.gold_purity || '');
        setGrossWeight(data.gross_weight || '');
        setNetWeight(data.net_weight || '');
        setFinePercent(data.fine_percentage ? parseFloat(data.fine_percentage) : null);
        setKundanWeight(data.kundan_weight ? String(parseFloat(data.kundan_weight)) : '');
        setKundanPercent(data.kundan_percentage ? String(parseFloat(data.kundan_percentage)) : '');
        setJewelryImage(data.image_url || null);
        setCertificateDocuments(data.certificates || []);

        // Set certification requirement from jewelry_pieces table
        setCertificationRequired(data.certificate === 'Yes');
        setLaborCharges(data.labor_charges ? parseFloat(data.labor_charges) : 0);
        setOtherCosts(data.other_costs ? parseFloat(data.other_costs) : 0);
        setInventoryNetWeight(data.net_weight || '');

        // Auto-populate stones
        const stones = data.stones || [];
        setFetchedStones(stones);

        // Calculate total diamond carats if certification is required
        if (data.certificate === 'Yes') {
          await calculateDiamondCarats(data.id, stones);
        } else {
          setDiamondCarats(0);
        }
      } else {
        setItemCodeStatus('not_found');
        setItemCodeStatusMsg('Item Not Found');
        setFetchedStones([]);
        setCertificateDocuments([]);
        setDiamondCarats(0);
        setCertificationRequired(false);
        setLaborCharges(0); setOtherCosts(0); setInventoryNetWeight('');
        itemCodeRef.current?.focus();
      }

      // Find category by code to get wastage/making/certification charges
      if (categoryCode && categories.length > 0) {
        const cat = categories.find(c => c.code === categoryCode);
        if (cat) {
          // Inherit from parent if child sale rates are 0 / not set
          const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
          const effectiveWastage = parseFloat(cat.sale_wastage_pct) > 0
            ? cat.sale_wastage_pct
            : (parent ? parent.sale_wastage_pct : '');
          const effectiveMaking = parseFloat(cat.sale_making_per_g) > 0
            ? cat.sale_making_per_g
            : (parent ? parent.sale_making_per_g : '');
          setCategoryInfo(cat);
          setWastagePercent(effectiveWastage || '');
          setMakingCharge(effectiveMaking || '');
        } else {
          // If direct match not found, try finding by name
          const catByName = categories.find(c => c.name?.toUpperCase().includes(categoryCode));
          if (catByName) {
            const parent = catByName.parent_id ? categories.find(c => c.id === catByName.parent_id) : null;
            const effectiveWastage = parseFloat(catByName.sale_wastage_pct) > 0
              ? catByName.sale_wastage_pct
              : (parent ? parent.sale_wastage_pct : '');
            const effectiveMaking = parseFloat(catByName.sale_making_per_g) > 0
              ? catByName.sale_making_per_g
              : (parent ? parent.sale_making_per_g : '');
            setCategoryInfo(catByName);
            setWastagePercent(effectiveWastage || '');
            setMakingCharge(effectiveMaking || '');
          } else {
            setCategoryInfo(null);
          }
        }
      }

      // Estimate overrides — applied last so they always win over system defaults
      if (estimateOverrides && itemFound) {
        if (estimateOverrides.stones) {
          // Estimates only snapshot sale price, not purchase cost — carry the real
          // cost_price forward from the item's current inventory stones (matched by
          // code) so the internal Cost Panel can still compute stone purchase cost.
          const inventoryStones = data.stones || [];
          const withCostPrice = estimateOverrides.stones.map(s => {
            const match = inventoryStones.find(inv => inv.stone_code === s.stone_code);
            return { ...s, cost_price: match ? match.cost_price : 0 };
          });
          setFetchedStones(withCostPrice);
        }
        if (estimateOverrides.wastage) setWastagePercent(estimateOverrides.wastage);
        if (estimateOverrides.making) setMakingCharge(estimateOverrides.making);
      }
    } catch (err) {
      console.error('Failed to fetch jewelry details', err);
      setItemCodeStatus('not_found');
      setItemCodeStatusMsg('Item Not Found');
      setFetchedStones([]);
      setDiamondCarats(0);
      setCertificationRequired(false);
      setLaborCharges(0); setOtherCosts(0); setInventoryNetWeight('');
      itemCodeRef.current?.focus();
    }
  };

  // Calculate diamond carats based on materials table and jewelry_stones
  const calculateDiamondCarats = async (jewelryId, stones) => {
    try {
      // Fetch from both Diamond and Flat Diamond categories
      const [diamondRes, flatDiamondRes] = await Promise.all([
        fetch('/api/materials/category/Diamond', { credentials: 'include' }),
        fetch('/api/materials/category/Flat Diamond', { credentials: 'include' })
      ])
      const [diamondData, flatDiamondData] = await Promise.all([
        diamondRes.json(),
        flatDiamondRes.json()
      ])

      const allDiamondMaterials = [
        ...(Array.isArray(diamondData) ? diamondData : []),
        ...(Array.isArray(flatDiamondData) ? flatDiamondData : [])
      ]

      if (allDiamondMaterials.length > 0) {
        const diamondCodes = allDiamondMaterials.map(material => material.code)
        console.log('Diamond codes from materials:', diamondCodes)

        // Filter stones that match diamond codes and calculate total weight
        const diamondStones = stones.filter(stone =>
          diamondCodes.includes(stone.stone_code)
        )

        const totalDiamondCarats = diamondStones.reduce((total, stone) =>
          total + (parseFloat(stone.weight) || 0), 0
        )

        console.log('Diamond stones found:', diamondStones)
        console.log('Total diamond carats:', totalDiamondCarats)

        setDiamondCarats(totalDiamondCarats)
      } else {
        setDiamondCarats(0)
      }
    } catch (err) {
      console.error('Failed to calculate diamond carats:', err)
      setDiamondCarats(0)
    }
  }

  const saveBilling = async (paymentsOverride = null) => {
    // Validate required fields
    if (!name || name.trim() === '') {
      alert('Please enter customer name');
      return null;
    }

    if (!grandTotal || parseFloat(grandTotal) <= 0) {
      alert('Please calculate the grand total before continuing');
      return null;
    }

    setIsSaving(true);

    try {
      // Prepare billing data
      const billingData = {
        customer_name: name,
        customer_phone: phone || null,
        customer_email: email || null,
        customer_address: null,
        customer_city: city || null,
        customer_state: state || null,
        item_code: itemCode || null,
        item_name: null,
        jewelry_category: categoryInfo?.name || null,
        gold_purity: purity || null,
        gross_weight: parseFloat(grossWeight) || null,
        stone_weight: parseFloat(stoneWeightTotal) || null,
        net_weight: parseFloat(netWeight) || null,
        gold_rate_24k: parseFloat(goldRate) || null,
        gold_value: parseFloat(goldValue) || null,
        stone_total: parseFloat(stoneTotal) || 0,
        making_charges: parseFloat(makingAmount) || 0,
        other_charges: parseFloat(wastageAmount) || 0,
        discount_amount: parseFloat(pdfTotals.discountAmount) || 0,
        grand_total: parseFloat(grandTotal),
        currency: 'INR',
        customs_percent: parseFloat(pdfTotals.gst) || 0,
        customs_amount: parseFloat(pdfTotals.taxTotal) || 0,
        base_total: Math.max(0, (parseFloat(grandTotal) || 0) - (parseFloat(pdfTotals.taxTotal) || 0)),
        stones: fetchedStones.length > 0 ? fetchedStones : null,
        notes: null,
        ...(estimateNumber ? { estimate_number: estimateNumber } : {}),
      };

      const response = await fetch('/api/billings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(billingData)
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Billing saved: ${result.data.bill_number}${itemCode ? ' | Item marked as SOLD' : ''}`);

        // Post payment rows — awaited so a rejected payment surfaces instead of
        // silently leaving the bill short-paid (shows "Completed" but saves as "partial").
        const billingId = result.data.id;
        const validPayments = (paymentsOverride || payments).filter(p => parseFloat(p.amount) > 0);
        const failedPayments = [];
        for (const p of validPayments) {
          try {
            const payRes = await fetch(`/api/billings/${billingId}/payments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                payment_type: p.payment_type,
                amount: parseFloat(p.amount),
                reference_number: p.reference_number || null,
                payment_date: p.payment_date || null,
                notes: p.notes || null,
                old_gold_items: p.old_gold_items || [],
              }),
            });
            const payData = await payRes.json().catch(() => ({}));
            if (!payRes.ok || !payData.success) {
              failedPayments.push(`${p.payment_type} ${p.amount}: ${payData.error || payRes.status}`);
            }
          } catch (err) {
            failedPayments.push(`${p.payment_type} ${p.amount}: ${err.message}`);
          }
        }
        if (failedPayments.length > 0) {
          alert(
            `Bill ${result.data.bill_number} was saved, but these payments could not be recorded:\n\n` +
            failedPayments.join('\n') +
            `\n\nThe bill will show as partially paid. Please add the missing payments from the bill.`
          );
        }

        // Record scheme redemptions for any gold_scheme payments
        const schemePayments = validPayments.filter(p => p.payment_type === 'gold_scheme' && p.enrollment_id);
        for (const p of schemePayments) {
          fetch('/api/schemes/redemptions/partial', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              enrollment_id: p.enrollment_id,
              amount: parseFloat(p.amount),
              purpose: `Billing #${result.data.bill_number}`,
            }),
          }).catch(err => console.warn('Scheme redemption post failed:', err));
        }

        // Mark linked estimate as converted
        if (estimateId) {
          fetch(`/api/estimates/${estimateId}/convert`, { method: 'POST', credentials: 'include' })
            .catch(err => console.warn('Could not mark estimate as converted:', err));
          setEstimateId(null);
          setEstimateNumber('');
          setEstimateBannerVisible(false);
        }

        return result.data; // Return the saved billing data
      } else {
        alert(`Failed to save billing: ${result.error}\n${result.details ? result.details.join(', ') : ''}`);
        return null;
      }
    } catch (error) {
      console.error('Error saving billing:', error);
      alert('Failed to save billing. Please check your connection.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle view billing
  // eslint-disable-next-line no-unused-vars
  const handleViewBilling = () => {
    if (!itemCode) {
      alert('Please fill in the item code to view billing');
      return;
    }
    setShowDetailModal(true);
  };

  const handleProceedToPayment = () => {
    if (!name || name.trim() === '') { alert('Please enter customer name'); return; }
    if (!grandTotal || parseFloat(grandTotal) <= 0) { alert('Please calculate the grand total before continuing'); return; }
    setShowPaymentModal(true);
  };

  const handleDownloadInvoice = async () => {
    if (!name || name.trim() === '') { alert('Please enter customer name'); return; }
    const stoneCategoryTotals = buildStoneCategoryTotals(fetchedStones);
    const colorStoneCarats = fetchedStones
      .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - (diamondCarats || 0);
    let previewBillNumber = '';
    try {
      const res = await fetch('/api/billings/next-number/INR', { credentials: 'include' });
      const result = await res.json();
      if (result.success) previewBillNumber = result.data.billNumber;
    } catch (e) { /* preview number is optional */ }
    await PdfService.downloadInvoicePDF({
      currency: 'INR',
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      companyTagline: 'Hallmarked Certified Jewellery',
      date,
      billNumber: previewBillNumber,
      estimateNumber: previewBillNumber,
      customerName: name,
      customerPhone: phone,
      customerEmail: email,
      customerCity: city,
      customerState: state,
      itemCode,
      purity,
      goldPrice,
      grossWeight,
      netWeight,
      makingCharge,
      makingAmount,
      totalGoldAmount,
      stoneCategoryTotals,
      certificateDocuments,
      diamondCarats: diamondCarats || 0,
      colorStoneCarats: Math.max(0, colorStoneCarats),
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      taxRate: pdfTotals.gst,
      taxAmount: pdfTotals.taxTotal,
      grandTotal,
      grandTotalINR: grandTotal,
      notes: '',
    }, 'INR');
  };

  const sendEmail = async (savedBilling) => {
    const stoneCategoryTotals = buildStoneCategoryTotals(fetchedStones);
    const colorStoneCarats = fetchedStones
      .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - (diamondCarats || 0);
    await EmailSendService.sendEmail({
      currency: 'INR',
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      companyTagline: 'Hallmarked Certified Jewellery',
      date,
      billNumber: savedBilling.bill_number,
      estimateNumber: savedBilling.bill_number,
      customerName: name,
      customerPhone: phone,
      customerEmail: email,
      customerCity: city,
      customerState: state,
      itemCode,
      jewelryName: jewelryName || itemCode,
      purity,
      grossWeight,
      netWeight,
      fineWeight,
      goldRate,
      goldPrice,
      goldValue,
      wastagePercent,
      wastageAmount,
      makingCharge,
      makingAmount,
      totalGoldAmount,
      stoneCategoryTotals,
      certificateDocuments,
      diamondCarats: diamondCarats || 0,
      colorStoneCarats: Math.max(0, colorStoneCarats),
      stoneTotal,
      certificationCharges: pdfTotals.certCharges,
      taxRate: pdfTotals.gst,
      taxLabel: `GST (${pdfTotals.gst || 0}%)`,
      taxAmount: pdfTotals.taxTotal,
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      grandTotal,
      grandTotalINR: grandTotal,
      notes: '',
      image_url: jewelryImage,
    }, email);
  };

  const sendWhatsApp = async (savedBilling) => {
    await WhatsAppLinkService.shareInvoice({
      customerName: name || 'Customer',
      phoneNumber: phone,
      date,
      itemCode,
      purity,
      grossWeight,
      netWeight,
      goldRate,
      wastagePercent,
      wastageAmount,
      makingCharge,
      makingAmount,
      goldAmount: totalGoldAmount,
      stoneAmount: stoneTotal,
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      grandTotal,
      billNumber: savedBilling.bill_number,
      currency: 'INR',
      companyName: 'GDS Jewellers',
      companyAddress: '15520 Pawnee St, Overland Park, KS - 66224',
    }, phone);
  };

  const clearForm = () => {
    // Clear customer fields
    setName('');
    setPhone('');
    setEmail('');
    setCity('');
    setState('');

    // Clear jewelry fields
    setItemCode('');
    setJewelryName('');
    setPurity('');
    setGrossWeight('');
    setNetWeight('');
    setFineWeight('');
    setFinePercent(null);
    setKundanWeight('');
    setKundanPercent('');
    setGoldPrice('');
    setGoldValue('');
    setWastagePercent('');
    setWastageAmount('');
    setMakingCharge('');
    setMakingAmount('');
    setTotalGoldAmount('');
    setFetchedStones([]);
    setStoneTotal(0);
    setStoneWeightTotal(0);
    setStonePurchaseCost(0);
    setJewelryImage(null);

    // Reset grand total and payments
    setGrandTotal('0.00');
    setPayments([]);
    setCustomerId(null);
    setEstimateDiscount(0);
    setEstimateGstPct(null);

    // Reset item code status
    setItemCodeStatus(null);
    setItemCodeStatusMsg('');

    // Reset certification data
    setCategoryInfo(null);
    setDiamondCarats(0);
    setCertificationRequired(false);
    setLaborCharges(0); setOtherCosts(0); setInventoryNetWeight('');

    // Reset gold rate back to the live default (was possibly overridden by a loaded estimate)
    setGoldRate(todaysGoldRate || '');
    setGoldRateEditable(false);

    // Clear any estimate this bill was linked to via Load Estimate / Convert to Sale
    setEstimateNumber('');
    setEstimateId(null);
    setEstimateBannerVisible(false);

    // Force TotalSection to remount so its own local state (discount, GST%)
    // resets too — those have no other reset path from here.
    setFormResetKey(k => k + 1);

    if (itemCodeRef.current) {
      itemCodeRef.current.focus();
    }
  };

  const handleLoadEstimate = (est) => {
    // Customer
    setName(est.customer_name || '');
    setPhone(est.customer_phone || '');
    setEmail(est.customer_email || '');

    // Rule 1: Gold rate — use whichever is higher (today vs quoted)
    const sysGold = parseFloat(goldRate) || 0;
    const estGold = parseFloat(est.gold_rate) || 0;
    const appliedGold = Math.max(sysGold, estGold);
    if (appliedGold > 0) { setGoldRate(appliedGold); setGoldRateEditable(true); }

    // Remap estimate stone format { code, name, rate } → billing format { stone_code, stone_name, sale_price }
    const estStones = Array.isArray(est.stone_details) && est.stone_details.length > 0
      ? est.stone_details.map(s => ({
          stone_code: s.stone_code || s.code || '',
          stone_name: s.stone_name || s.name || '',
          weight: s.weight,
          unit: s.unit || 'gram',
          sale_price: s.sale_price ?? s.rate ?? 0,
        }))
      : null;
    const estWastage = est.wastage_percent ? String(est.wastage_percent) : null;
    const estMaking = est.making_charge_per_gram ? String(est.making_charge_per_gram) : null;

    if (est.item_code) {
      setItemCode(est.item_code);
      // Pass estimate values as overrides — fetchJewelryDetails applies them after all system defaults
      setTimeout(() => fetchJewelryDetails(est.item_code, {
        stones: estStones,
        wastage: estWastage,
        making: estMaking,
      }), 300);
    } else {
      if (estStones) setFetchedStones(estStones);
      if (estWastage) setWastagePercent(estWastage);
      if (estMaking) setMakingCharge(estMaking);
    }

    setEstimateNumber(est.estimate_number || '');
    setEstimateId(est.id || null);
    setEstimateDiscount(parseFloat(est.discount_amount) || 0);
    setEstimateGstPct(est.tax_rate != null ? parseFloat(est.tax_rate) : null);
    setEstimateBannerVisible(true);
    setShowLoadModal(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageIdentifier pageId={SCREEN_IDS?.BILLING?.INDIAN_BILLING || 'BILL-001'} pageName="Indian Billing" />

      {/* ── Estimate pre-fill banner ── */}
      {estimateBannerVisible && estimateNumber && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between print:hidden">
          <span className="text-xs text-amber-800 font-medium">
            📋 Pre-filled from estimate <strong>{estimateNumber}</strong>. Confirm details and save to complete the sale.
          </span>
          <button
            onClick={() => setEstimateBannerVisible(false)}
            className="text-amber-500 hover:text-amber-700 text-xs underline ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Sticky Action Bar ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="px-4 py-2.5 flex items-center justify-between gap-3 max-w-5xl mx-auto">

          {/* Left: Title + secondary actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <h1 className="text-base font-bold text-slate-800 whitespace-nowrap flex items-center gap-2">
              <Gem size={16} className="text-amber-500" />
              INR Billing
            </h1>
            <div className="w-px h-5 bg-slate-200" />
            <button onClick={() => setShowLoadModal(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 transition whitespace-nowrap">
              <Search size={12} /> Load Estimate
            </button>
            <button onClick={clearForm}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition">
              Clear
            </button>
          </div>

          {/* Centre: Primary actions */}
          <div className="flex items-center gap-1.5">
            <button onClick={handleDownloadInvoice} disabled={isSaving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-40 transition whitespace-nowrap">
              <FileText size={13} />
              Invoice PDF
            </button>
            <button onClick={handleProceedToPayment} disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 transition whitespace-nowrap">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              Checkout
            </button>
          </div>

          {/* Right: Gold Rate pill + Date pill */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Gold Rate pill */}
            <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-amber-600 whitespace-nowrap">24K Gold</span>
              <div className="w-px h-4 bg-amber-200" />
              {goldRateEditable ? (
                <div className="flex items-center gap-0.5">
                  <span className="text-sm font-bold text-amber-800">₹</span>
                  <input type="number" onFocus={e => e.target.select()} value={goldRate} onChange={(e) => setGoldRate(e.target.value)}
                    className="w-20 text-sm font-bold focus:outline-none bg-transparent text-amber-800 border-b border-amber-400" />
                </div>
              ) : (
                <span className="text-sm font-bold text-amber-800 whitespace-nowrap">
                  ₹{goldRate ? parseFloat(goldRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </span>
              )}
            </div>
            {/* Date pill */}
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input type="date" value={date} onChange={handleDateChange}
                className="text-xs font-medium text-slate-700 focus:outline-none bg-transparent cursor-pointer" />
            </div>
          </div>
        </div>
        {(goldRateError || goldRateWarn) && (
          <div className="px-4 pb-1.5 flex gap-3">
            {goldRateError && <p className="text-xs text-red-500">{goldRateError}</p>}
            {goldRateWarn && <p className="text-xs text-amber-500">{goldRateWarn}</p>}
          </div>
        )}
      </div>

      <div className="p-4 pb-20 max-w-6xl mx-auto">

        {/* ── Unified two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

          {/* ══ LEFT COLUMN ══ */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Customer Details */}
            <div className="relative z-10 bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <User className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Customer</span>
              </div>
              <div className="p-3">
                <CustomerLookup
                  customerName={name} customerPhone={phone}
                  customerEmail={email} customerCity={city} customerState={state}
                  country="India"
                  onChange={(field, value) => {
                    if (field === 'customerName') setName(value)
                    else if (field === 'customerPhone') setPhone(value)
                    else if (field === 'customerEmail') setEmail(value)
                    else if (field === 'customerCity') setCity(value)
                    else if (field === 'customerState') setState(value)
                  }}
                  onCustomerLoaded={(customer) => setCustomerId(customer?.id || null)}
                />
              </div>
            </div>

            {/* Jewelry & Gold Calculation */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <Gem className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Item & Gold</span>
              </div>
              <div className="p-3 space-y-2">
                {/* Item Code */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Item Code</label>
                  <div className="flex flex-col gap-0.5">
                    <input
                      type="text"
                      value={itemCode}
                      ref={itemCodeRef}
                      onChange={e => { setItemCode(e.target.value.toUpperCase()); setItemCodeStatus(null); setItemCodeStatusMsg(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchJewelryDetails(itemCode); e.target.blur(); } }}
                      className={`w-full border-2 rounded-lg px-3 py-1.5 text-sm font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${itemCodeStatus === 'sold' ? 'border-red-300 bg-red-50' : itemCodeStatus === 'not_found' ? 'border-yellow-300 bg-yellow-50' : 'border-amber-300 bg-amber-50'}`}
                      placeholder="e.g. DNS-1" />
                    {itemCodeStatus === 'sold' && <span className="text-xs font-semibold text-red-600">🔴 {itemCodeStatusMsg}</span>}
                    {itemCodeStatus === 'not_found' && <span className="text-xs font-semibold text-yellow-600">⚠️ {itemCodeStatusMsg}</span>}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Description</label>
                  <input type="text" value={jewelryName} onChange={e => setJewelryName(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder="Name" />
                </div>

                {/* Weight row */}
                <div className="grid grid-cols-3 gap-2">
                  {[['Gross Wt', grossWeight], ['Net Wt', netWeight], ['Fine Wt', fineWeight]].map(([lbl, val]) => (
                    <div key={lbl}>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{lbl}</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {val || <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Purity / Kundan Wt (Kundan only) or hidden / Gold Value */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Purity</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {purity ? `${purity}K` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  {isKundanCategory(categoryInfo) ? (
                    <div>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Kundan Wt</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {kundanWeight ? `${parseFloat(kundanWeight).toFixed(3)}g` : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Gold Value</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {goldValue ? `₹${parseFloat(goldValue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                </div>

                {/* VA % / Making/g — editable for manager/admin/super_admin */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA %</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} step="0.001" value={wastagePercent} onChange={e => setWastagePercent(e.target.value)} onKeyDown={blurOnEnter}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="VA %" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {wastagePercent || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making/g</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} value={makingCharge} onChange={e => setMakingCharge(e.target.value)} onKeyDown={blurOnEnter}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="Making/g" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {makingCharge || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* VA Amt / Making Amt / Total Gold */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {wastageAmount || <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {makingAmount || <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Total Gold</label>
                    <div className="w-full border border-amber-200 rounded-lg px-3 py-1.5 text-sm bg-amber-50 text-amber-800 font-bold">
                      {totalGoldAmount ? `₹${parseFloat(totalGoldAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-amber-300">—</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stone Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <Gem className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Stone Details</span>
              </div>
              <StoneSection
                onStoneTotalChange={({ saleTotal, purchaseTotal, totalWeight }) => {
                  setStoneTotal(saleTotal);
                  setStonePurchaseCost(purchaseTotal);
                  setStoneWeightTotal(totalWeight || 0);
                }}
                initialStones={fetchedStones}
              />
            </div>
          </div>{/* end left col */}

          {/* ══ RIGHT COLUMN — sticky ══ */}
          <div className="lg:col-span-1 flex flex-col gap-4 sticky top-[60px] self-start">
            <TotalSection
              key={formResetKey}
              totalGoldAmount={totalGoldAmount}
              goldValue={goldValue}
              wastageAmount={wastageAmount}
              makingAmount={makingAmount}
              stoneTotal={stoneTotal}
              stonePurchaseCost={stonePurchaseCost}
              diamondCarats={diamondCarats}
              certificationRequired={certificationRequired}
              setCertificationRequired={setCertificationRequired}
              setDiamondCarats={setDiamondCarats}
              categoryInfo={categoryInfo}
              setParentGrandTotal={setGrandTotal}
              onTotalsChange={setPdfTotals}
              itemCode={itemCode}
              grossWeight={grossWeight}
              netWeight={netWeight}
              fineWeight={fineWeight}
              goldRate={goldRate}
              purity={purity}
              finePercent={finePercent}
              kundanWeight={kundanWeight}
              kundanPercent={kundanPercent}
              laborCharges={laborCharges}
              otherCosts={otherCosts}
              inventoryNetWeight={inventoryNetWeight}
              fetchedStones={fetchedStones}
              todaysGoldRate={todaysGoldRate}
              billDate={date}
              estimateDiscount={estimateDiscount}
              initialGstPct={estimateGstPct}
            />

            {/* Item Image */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <svg className="text-slate-300 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Item Image</span>
              </div>
              {jewelryImage ? (
                <img
                  src={jewelryImage}
                  alt={itemCode || 'Jewelry'}
                  className="w-full object-contain"
                />
              ) : (
                <div className="w-full aspect-square bg-slate-50 flex flex-col items-center justify-center gap-3">
                  <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-slate-300 text-xs tracking-wide">Enter item code to load image</span>
                </div>
              )}
            </div>
          </div>

        </div>

      </div>

      {/* Detail Modal */}
      <EstimateDetailModal
        isOpen={showDetailModal}
        estimate={{
          is_billing_draft: true,
          estimate_number: 'DRAFT', customer_name: name || 'Customer',
          customer_phone: phone, customer_email: email,
          customer_city: city, customer_state: state,
          date, item_code: itemCode,
          gold_purity: purity, gross_weight: grossWeight,
          net_weight: netWeight, fine_weight: fineWeight,
          gold_rate: goldRate, gold_price: goldPrice,
          gold_value: goldValue, wastage_percent: wastagePercent,
          wastage_amount: wastageAmount, making_charge_per_gram: makingCharge,
          making_amount: makingAmount, total_gold_amount: totalGoldAmount,
          stone_total: stoneTotal, diamond_carats: diamondCarats || 0,
          color_stone_carats: Math.max(0, fetchedStones
            .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
            .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - (diamondCarats || 0)),
          certification_required: certificationRequired,
          currency: 'INR', grand_total: grandTotal, notes: ''
        }}
        onClose={() => setShowDetailModal(false)}
      />

      <PaymentConfirmModal
        isOpen={showPaymentModal}
        onClose={(wasSaved) => { setShowPaymentModal(false); if (wasSaved) clearForm(); }}
        grandTotal={parseFloat(grandTotal) || 0}
        currency="INR"
        customerId={customerId}
        goldRate={parseFloat(goldRate) || null}
        customerName={name}
        customerPhone={phone}
        customerEmail={email}
        onSaveAndConfirm={(pmts) => saveBilling(pmts)}
        onSendEmail={sendEmail}
        onSendWhatsApp={sendWhatsApp}
      />
      {showLoadModal && (
        <LoadEstimateModal
          currency="INR"
          onClose={() => setShowLoadModal(false)}
          onLoad={handleLoadEstimate}
        />
      )}
    </div>
  );
};

// Stone Section Component
function StoneSection({ onStoneTotalChange, initialStones = [] }) {
  const [stones, setStones] = useState([]);

  // Accept initial stones from parent
  useEffect(() => {
    if (Array.isArray(initialStones) && initialStones.length > 0) {
      setStones(initialStones.map((stone, idx) => {
        const weight = parseFloat(stone.weight) || 0;
        const unit = stone.unit || 'CARAT';
        return {
          id: stone.id || idx,
          code: stone.stone_code || '',
          name: stone.stone_name || '',
          weight,
          unit,
          purchaseRate: parseFloat(stone.cost_price) || 0,
          rate: parseFloat(stone.sale_price) || 0,
          cost: weight * (parseFloat(stone.sale_price) || 0),
          purchaseCost: weight * (parseFloat(stone.cost_price) || 0),
        };
      }));
    } else {
      // Clear stones when initialStones is empty
      setStones([]);
    }
  }, [initialStones]);

  useEffect(() => {
    const total = stones.reduce((sum, stone) => sum + parseFloat(stone.cost || 0), 0);
    const purchaseTotal = stones.reduce((sum, stone) => sum + parseFloat(stone.purchaseCost || 0), 0);
    // Unit-aware: convert each stone's weight to grams before summing
    const totalWeight = stones.reduce((sum, stone) => sum + stoneWeightToGrams(stone.weight, stone.unit), 0);
    onStoneTotalChange({ saleTotal: total, purchaseTotal, totalWeight });
  }, [stones, onStoneTotalChange]);

  const updateStone = (id, field, value) => {
    setStones(stones.map(stone => {
      if (stone.id === id) {
        const updatedStone = { ...stone, [field]: parseFloat(value) || 0 };
        if (field === 'rate') {
          updatedStone.cost = updatedStone.weight * updatedStone.rate;
        }
        return updatedStone;
      }
      return stone;
    }));
  };

  return (
    <div className="space-y-2">
      {stones.length > 0 && (
        <div className="mt-1">
          <div className="overflow-x-auto">
            <table className="min-w-full border border-slate-200 text-xs">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="border border-slate-600 px-2 py-1.5 text-left font-semibold whitespace-nowrap">Stone Code</th>
                  <th className="border border-slate-600 px-2 py-1.5 text-left font-semibold whitespace-nowrap">Stone Name</th>
                  <th className="border border-slate-600 px-2 py-1.5 text-right font-semibold whitespace-nowrap">Weight</th>
                  <th className="border border-slate-600 px-2 py-1.5 text-center font-semibold whitespace-nowrap">Unit</th>
                  <th className="border border-slate-600 px-2 py-1.5 text-right font-semibold whitespace-nowrap">Rate (₹)</th>
                  <th className="border border-slate-600 px-2 py-1.5 text-right font-semibold whitespace-nowrap">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {[...stones].sort((a, b) => {
                  const u = s => (s.unit || '').toUpperCase();
                  const unitOrder = u => (u === 'CARAT' || u === 'CARATS' || u === 'CT') ? 0 : (u === 'GRAM' || u === 'GRAMS' || u === 'G') ? 1 : 2;
                  const uDiff = unitOrder(u(a)) - unitOrder(u(b));
                  if (uDiff !== 0) return uDiff;
                  return (a.code || '').localeCompare(b.code || '');
                }).map((stone, idx) => (
                  <tr key={stone.id} className={idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100/60'}>
                    <td className="border border-slate-200 px-2 py-1.5 font-mono font-medium text-slate-700">{stone.code}</td>
                    <td className="border border-slate-200 px-2 py-1.5 text-slate-600">{stone.name}</td>
                    <td className="border border-slate-200 px-2 py-1.5 text-right text-slate-700">
                      {(() => { const u = (stone.unit || '').toUpperCase(); return u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER' ? stone.weight : parseFloat(stone.weight).toFixed(2); })()}
                    </td>
                    <td className="border border-slate-200 px-2 py-1.5 text-center text-slate-600">
                      {(() => { const u = (stone.unit || '').toUpperCase(); return u === 'CARAT' || u === 'CARATS' || u === 'CT' ? 'Ct' : u === 'GRAM' || u === 'GRAMS' || u === 'G' ? 'g' : u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER' ? 'pcs' : stone.unit || 'Ct'; })()}
                    </td>
                    <td className="border border-slate-200 px-1.5 py-1">
                      <input
                        type="number" onFocus={e => e.target.select()}
                        value={stone.rate}
                        onChange={e => updateStone(stone.id, 'rate', e.target.value)}
                        className="w-24 text-right border border-slate-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                        min="0"
                        step="1"
                      />
                    </td>
                    <td className="border border-slate-200 px-2 py-1.5 text-right font-medium text-slate-700">
                      ₹{parseFloat(stone.cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold">
                  <td colSpan={5} className="border border-slate-200 px-2 py-1.5 text-right text-slate-700 text-xs">Total Stone Amount</td>
                  <td className="border border-slate-200 px-2 py-1.5 text-right text-slate-800 text-xs">
                    ₹{stones.reduce((sum, stone) => sum + stone.cost, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      {stones.length === 0 && (
        <div className="text-center text-slate-400 py-4 text-sm">
          No stones data available
        </div>
      )}
    </div>
  );
}

// Total Section Component
// Total Section Component - Modified for Indian Billing with Certification Charges
function TotalSection({ totalGoldAmount, goldValue, wastageAmount, makingAmount, stoneTotal, stonePurchaseCost, diamondCarats: propDiamondCarats, certificationRequired: propCertificationRequired, setCertificationRequired, setDiamondCarats, categoryInfo, setParentGrandTotal, onTotalsChange, itemCode = '', grossWeight, netWeight, fineWeight, goldRate, purity, finePercent, kundanWeight, kundanPercent, fetchedStones, laborCharges = 0, otherCosts = 0, inventoryNetWeight = '', todaysGoldRate = null, billDate = '', estimateDiscount = 0, initialGstPct = null }) {
  const { hasPermission } = usePermissions();
  const isUser = !hasPermission('indian-billing', 'edit');
  const [certChargePerCarat, setCertChargePerCarat] = useState(0); // Will be set from category
  const [gst, setGst] = useState(3);
  const [taxTotal, setTaxTotal] = useState('0.00');
  const [grandTotal, setGrandTotal] = useState('0.00');
  const [certificationRequired, setCertificationRequiredLocal] = useState(propCertificationRequired || false);
  const [diamondCarats, setDiamondCaratsLocal] = useState(propDiamondCarats || 0);
  const [calculatedCertCharges, setCalculatedCertCharges] = useState(0);
  const [discount, setDiscount] = useState(0);

  // Load Estimate / Convert to Billing apply discount and GST% as prop
  // updates (this component is already mounted by then), not just an
  // initial value — sync them in when they change.
  useEffect(() => {
    if (estimateDiscount > 0) setDiscount(estimateDiscount);
  }, [estimateDiscount]);

  useEffect(() => {
    if (initialGstPct != null && parseFloat(initialGstPct) > 0) {
      setGst(parseFloat(initialGstPct));
    }
  }, [initialGstPct]);

  // Hold-to-peek cost panel (hold backtick ` key) — admin/manager only
  const [showCostPanel, setShowCostPanel] = useState(false);
  useEffect(() => {
    if (isUser) return; // disabled for role=user
    const onDown = (e) => { if (e.key === '`') { e.preventDefault(); if (!e.repeat) setShowCostPanel(true); } };
    const onUp = (e) => { if (e.key === '`') setShowCostPanel(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [isUser]);

  // Update certification charge per carat when category changes
  useEffect(() => {
    if (categoryInfo && categoryInfo.certification_charges) {
      setCertChargePerCarat(categoryInfo.certification_charges);
    } else {
      setCertChargePerCarat(0);
    }
  }, [categoryInfo]);

  // Sync with parent props
  useEffect(() => {
    setCertificationRequiredLocal(propCertificationRequired || false);
  }, [propCertificationRequired]);

  useEffect(() => {
    setDiamondCaratsLocal(propDiamondCarats || 0);
  }, [propDiamondCarats]);

  // Update parent when local state changes
  // const handleCertificationChange = (value) => { // Not used in current implementation
  //   setCertificationRequiredLocal(value);
  //   if (setCertificationRequired) {
  //     setCertificationRequired(value);
  //   }
  // };

  // const handleDiamondCaratsChange = (value) => { // Not used in current implementation
  //   setDiamondCaratsLocal(value);
  //   if (setDiamondCarats) {
  //     setDiamondCarats(value);
  //   }
  // };

  // Fetch tax rates on mount
  useEffect(() => {
    fetch('/api/rates/tax/latest', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate && data.rate.gst_percentage) {
          setGst(data.rate.gst_percentage);
        }
      })
      .catch(() => {
        // Keep default value on error
      });
  }, []);

  // Small items ("V" category): priced as cost + 30%, rounded up to ₹100.
  const smallItem = isSmallItemCode(itemCode);

  const actualCostINR = useMemo(() => {
    const isKundan = isKundanItemCode(itemCode);
    const nw = parseFloat(inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
    const kw = parseFloat(kundanWeight) || 0;
    const goldNetWt = parseFloat(inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
    const kundanFineWt = isKundan && finePercent > 0
      ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
      : 0;
    const rate24k = parseFloat(goldRate) || 0;
    const goldCostAmt = isKundan && kundanFineWt > 0
      ? Math.ceil(kundanFineWt * rate24k)
      : Math.ceil(calculateGoldValue(calculatePurityGoldRate(rate24k, purity), nw));
    const stoneCostAmt = Math.ceil(parseFloat(stonePurchaseCost || 0));
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    const laborCost = Math.ceil(nw * (laborCharges || 0));
    const { totalCost } = calculateAcquisitionCostWithTax({ goldCost: goldCostAmt, stoneCost: stoneCostAmt, laborCost, certCost: certAmt, otherCost: otherCosts, taxPercent: gst });
    return totalCost;
  }, [itemCode, netWeight, kundanWeight, kundanPercent, finePercent, goldRate, stonePurchaseCost, diamondCarats, certChargePerCarat, certificationRequired, laborCharges, otherCosts, inventoryNetWeight, purity, gst]);

  const smallItemPrice = useMemo(
    () => (smallItem && actualCostINR > 0 ? calculateSmallItemPrice(actualCostINR, 100) : 0),
    [smallItem, actualCostINR]
  );

  // Calculate certification charges and totals
  useEffect(() => {
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    setCalculatedCertCharges(certAmt);

    const jewelValue = calculateJewelValue(totalGoldAmount, stoneTotal, certAmt);
    const normal = calculateINRGrandTotal(jewelValue, gst);
    const useSmall = smallItem && smallItemPrice > 0;
    const gstAmount = useSmall ? 0 : normal.gstAmount;
    // V items: cost + 30%, or the normal price when staff-raised making / VA /
    // stone rates push it higher (leaves room to negotiate down).
    const total = useSmall ? Math.max(smallItemPrice, normal.grandTotal) : normal.grandTotal;
    setTaxTotal(gstAmount.toFixed(2));
    setGrandTotal(total.toFixed(2));

    const discountAmt = Math.min(parseFloat(discount) || 0, total);
    const finalTotal = Math.max(0, total - discountAmt);

    if (setParentGrandTotal) {
      setParentGrandTotal(finalTotal);
    }
    if (onTotalsChange) {
      onTotalsChange({ gst: parseFloat(gst) || 0, taxTotal: gstAmount.toFixed(2), certCharges: certAmt, discountAmount: discountAmt, smallItem: useSmall });
    }
  }, [totalGoldAmount, stoneTotal, certChargePerCarat, gst, certificationRequired, diamondCarats, discount, setParentGrandTotal, onTotalsChange, smallItem, smallItemPrice]);

  return (
    <div className="space-y-3">

      {/* ── Price Breakdown Card ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-slate-700 px-4 py-2.5 flex items-center gap-2 rounded-t-xl">
          <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
          </svg>
          <span className="text-white font-semibold text-xs tracking-widest uppercase">Price Breakdown</span>
        </div>

        {/* Line items — hidden for small ("V") items: only weights + final price shown */}
        {!smallItem && (
        <div className="divide-y divide-slate-100">
          {/* Gold Amount */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">Gold Amount</span>
            </div>
            <span className="text-sm font-semibold text-slate-800">
              ₹{Math.ceil(parseFloat(totalGoldAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Stone Cost — only if non-zero */}
          {parseFloat(stoneTotal || 0) > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Stone Cost</span>
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{Math.ceil(parseFloat(stoneTotal || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Certification — only if non-zero */}
          {calculatedCertCharges > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Certification</span>
                {certChargePerCarat > 0 && (
                  <span className="text-xs text-slate-400">(₹{parseFloat(certChargePerCarat).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/ct)</span>
                )}
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{calculatedCertCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Subtotal */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
            <span className="text-sm font-medium text-slate-700">Subtotal</span>
            <span className="text-sm font-bold text-slate-800">
              ₹{(Math.ceil(parseFloat(totalGoldAmount || 0)) + Math.ceil(parseFloat(stoneTotal || 0)) + calculatedCertCharges).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* GST Row — editable % inline (manager/admin/super_admin only) */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">GST</span>
              {!isUser ? (
                <div className="flex items-center border border-amber-400 rounded-md overflow-hidden">
                  <input
                    type="number" onFocus={e => e.target.select()}
                    value={gst || ''}
                    onChange={e => setGst(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-12 text-center text-xs py-0.5 px-1 focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                  />
                  <span className="bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600 border-l border-amber-400">%</span>
                </div>
              ) : (
                <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded px-2 py-0.5">{gst || 0}%</span>
              )}
            </div>
            <span className="text-sm font-semibold text-slate-700">
              ₹{parseInt(taxTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        )}

        {/* Grand Total footer */}
        <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
          <p className="text-slate-300 text-xs uppercase tracking-widest font-medium">Grand Total (INR)</p>
          <span className="text-white text-2xl font-bold tracking-tight">
            ₹{parseInt(grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Discount row */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-300">Round Off</span>
            <div className="flex items-center border border-green-500 rounded overflow-hidden">
              <span className="bg-slate-600 px-1.5 py-0.5 text-xs text-green-400 border-r border-green-500">₹</span>
              <input
                type="number" onFocus={e => e.target.select()}
                value={discount || ''}
                onChange={e => setDiscount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                placeholder="0"
                min="0"
                className="w-20 text-center text-xs py-0.5 px-1 focus:outline-none bg-slate-700 text-white placeholder-slate-400"
              />
            </div>
            <button
              type="button"
              onClick={() => { const cur = parseFloat(grandTotal) || 0; setDiscount(cur % 100); }}
              className="text-xs px-2 py-0.5 rounded border border-green-500 bg-slate-600 text-green-400 hover:bg-slate-500 transition whitespace-nowrap"
            >
              Round ↓100
            </button>
          </div>
          {parseFloat(discount) > 0 && (
            <span className="text-sm font-semibold text-green-400">
              −₹{parseFloat(discount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>

        {/* Final Amount */}
        {parseFloat(discount) > 0 && (
          <div className="bg-emerald-700 px-4 py-3 flex items-center justify-between">
            <p className="text-emerald-100 text-xs uppercase tracking-widest font-medium">Final Amount (INR)</p>
            <span className="text-white text-2xl font-bold tracking-tight">
              ₹{Math.round(Math.max(0, (parseFloat(grandTotal) || 0) - (parseFloat(discount) || 0))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>

      {/* ── CGST / SGST split pill ── */}
      <div className="flex gap-2">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">CGST ({(parseFloat(gst || 0) / 2).toFixed(1)}%)</p>
          <p className="text-sm font-bold text-slate-700 mt-0.5">₹{Math.ceil(parseFloat(taxTotal || 0) / 2).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">SGST ({(parseFloat(gst || 0) / 2).toFixed(1)}%)</p>
          <p className="text-sm font-bold text-slate-700 mt-0.5">₹{Math.ceil(parseFloat(taxTotal || 0) / 2).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* ── HOLD-TO-PEEK COST PANEL — Kundan items only (print:hidden, owner only) ── */}
      {showCostPanel && (() => {
        const isKundan = isKundanItemCode(itemCode);
        if (!isKundan) return null;

        const _gw = parseFloat(grossWeight) || 0; // eslint-disable-line no-unused-vars
        const nw = parseFloat(inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
        const kw = parseFloat(kundanWeight) || 0;
        const goldNetWt = parseFloat(inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
        const rate24k = parseFloat(goldRate) || 0;

        const fw = finePercent > 0
          ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
          : calculateFineWeight(nw, purity);

        const goldCost = Math.ceil(fw * rate24k);
        const stoneCost = Math.ceil(parseFloat(stonePurchaseCost) || 0);
        const certCost = calculatedCertCharges;
        const laborCost = Math.ceil(nw * (laborCharges || 0));
        const { preTaxCost, taxAmount: taxCost } = calculateAcquisitionCostWithTax({ goldCost, stoneCost, laborCost, certCost, otherCost: otherCosts, taxPercent: gst });

        const quotedCost = parseFloat(grandTotal || 0);
        const { actualCost, profit, profitPct } = calculateCostPanelResult({ preTaxCost, taxAmount: taxCost, quotedCost });

        return (
          <CostPanel
            positionClassName="top-4 right-4"
            widthClassName="w-72"
            ratePill={
              <RatePill
                todayGoldRate={todaysGoldRate}
                usedGoldRate={rate24k}
                usedDate={billDate}
              />
            }
            rows={[
              { label: 'Gold Cost', value: goldCost },
              { label: 'Stones Cost', value: stoneCost },
              { label: `Labor${laborCharges ? ` (₹${laborCharges}/g)` : ''}`, value: laborCost },
              { label: 'Certificate', value: certCost },
              { label: 'Other Costs', value: otherCosts },
              { label: 'Tax (GST)', value: taxCost },
            ]}
            quotedCost={quotedCost}
            actualCost={actualCost}
            profit={profit}
            profitPct={profitPct}
          />
        );
      })()}
    </div>
  );
}

export default IndianBilling;